/**
 * @typedef {Object} T_IdbOptions
 * @property {string} name
 * @property {number} version
 * @property {T_IdbStore[]} stores
 */

/**
 * @typedef {Object} T_IdbStore
 * @property {string} name
 * @property {string} [keyPath='']
 * @property {boolean} [autoIncrement=false]
 * @property {T_IdbIndex[]} [indices]
 */

/**
 * @typedef {Object} T_IdbIndex
 * @property {string} field
 * @property {boolean} [unique=false]
 */

export default class Idb {
	/**
	 * @param {T_IdbOptions} options
	 */
	constructor(options) {
		/**
		 * @type {T_IdbOptions}
		 * @private
		 */
		this._opt = options;

		/**
		 * @type {IDBDatabase|null}
		 * @private
		 */
		this._db = null;
	}

	/**
	 * @param {string} storeName
	 * @param {*} value
	 * @return {Promise<void>}
	 */
	async set(storeName, value) {
		return this._processStore(storeName, "readwrite", (store) => {
			store.put(value);
		});
	}

	/**
	 * @param {string} storeName
	 * @param {IDBValidKey} key
	 * @param {*} value
	 * @return {Promise<void>}
	 */
	async setByKey(storeName, key, value) {
		return this._processStore(storeName, "readwrite", (store) => {
			store.put(value, key);
		});
	}

	/**
	 * @param {string} storeName
	 * @param {Array} values
	 * @return {Promise<void>}
	 */
	async setArray(storeName, values) {
		return this._processStore(storeName, "readwrite", (store) => {
			for (const value of values) {
				store.put(value);
			}
		});
	}

	/**
	 * @param {string} storeName
	 * @param {Map<IDBValidKey,*>} values
	 * @return {Promise<void>}
	 */
	async setMap(storeName, values) {
		return this._processStore(storeName, "readwrite", (store) => {
			for (const [key, value] of values.entries()) {
				store.put(value, key);
			}
		});
	}

	/**
	 * @param {string} storeName
	 * @param {IDBValidKey} key
	 * @return {Promise<*>}
	 */
	async get(storeName, key) {
		return new Promise(async (resolve, reject) => {
			await this._processStore(storeName, "readonly", (store) => {
				const request = store.get(key);
				request.onsuccess = e => resolve(request.result);
				request.onerror = () => reject();
			});
		});
	}

	/**
	 * @param {string} storeName
	 * @param {IDBValidKey|IDBKeyRange|null} [query=null]
	 * @return {Promise<*>}
	 */
	async getArray(storeName, query = null) {
		return new Promise(async (resolve, reject) => {
			await this._processStore(storeName, "readonly", (store) => {
				const request = store.getAll(query);
				request.onsuccess = e => resolve(request.result);
				request.onerror = () => reject();
			});
		});
	}

	/**
	 * @param {string} storeName
	 * @param {IDBValidKey|IDBKeyRange|null} [query=null]
	 * @return {Promise<Map>}
	 */
	async getMap(storeName, query = null) {
		return new Promise(async (resolve, reject) => {
			await this._processStore(storeName, "readonly", (store) => {
				let out = new Map();
				const request = store.openCursor();

				request.onsuccess = (e) => {
					const cursor = e.target.result;
					if (cursor) {
						out.set(cursor.primaryKey, cursor.value);
						cursor.continue();
					} else {
						resolve(out);
					}
				};

				request.onerror = () => reject();
			});
		});
	}

	/**
	 * @param {string} storeName
	 * @param {IDBTransactionMode} mode
	 * @param {function(store: IDBObjectStore): void} handler
	 * @return {Promise<void>}
	 * @private
	 */
	async _processStore(storeName, mode, handler) {
		return new Promise(async (resolve, reject) => {
			const db = await this._getDb();
			const transaction = db.transaction(storeName, mode);
			transaction.onerror = () => reject();
			transaction.oncomplete = () => resolve();
			handler(transaction.objectStore(storeName));
		});
	}

	/**
	 * @return {Promise<IDBDatabase>}
	 * @private
	 */
	async _getDb() {
		if (this._db) {
			return this._db;
		}

		return new Promise(async (resolve, reject) => {
			const request = window.indexedDB.open(this._opt.name, this._opt.version);
			request.onsuccess = () => {
				this._db = request.result;
				resolve(this._db);
			};

			request.onerror = () => {
				reject();
			};

			request.onupgradeneeded = async () => {
				const db = request.result;
				await Promise.all(this._opt.stores.map(store => this._createStore(db, store)));
			};
		});
	}

	/**
	 * @param {IDBDatabase} db
	 * @param {T_IdbStore} store
	 * @return {Promise<void>}
	 * @private
	 */
	async _createStore(db, store) {
		return new Promise((resolve, reject) => {
			/** @type {IDBObjectStoreParameters} */
			const opts = {};

			if (store.keyPath) {
				opts.keyPath = store.keyPath;
			}

			if (store.autoIncrement) {
				opts.autoIncrement = true;
			}

			const objectsStore = db.createObjectStore(store.name, opts);
			objectsStore.transaction.onerror = () => reject();
			objectsStore.transaction.oncomplete = () => resolve();

			if (store.indices) {
				for (const index of store.indices) {
					objectsStore.createIndex(store.name + '__' + index.field, index.field, {unique: index.unique});
				}
			}
		});
	}
}
